import React from "react";
import { ClerkProvider, SignInButton, UserButton, Show, useAuth } from "@clerk/react";

/**
 * Signing in, and nothing else yet.
 *
 * Signing in and signing out, and nothing else — what a person's collection
 * then does about it lives in useSync.js. Kept apart because they went in
 * separately and for a reason: an account that can lose your collection is
 * worse than no account, so the half that cannot lose anything shipped first
 * and was watched for a while.
 *
 * Accounts are optional, and that is enforced here rather than promised: with
 * no publishable key configured the provider is not mounted, no Clerk code
 * runs, no request leaves the page, and the app is exactly what it was. That
 * also means this can ship before the keys exist.
 */

/* Read at build time by Create React App, so it is baked into the bundle.
   That is fine and intended — a publishable key is public by design, which is
   why it belongs in .env.production rather than the untracked .env: a fresh
   clone should build a working app without anyone having to remember. */
export const CLERK_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || "";

export const accountsEnabled = Boolean(CLERK_KEY);

const SIGNED_OUT = Object.freeze({
  enabled: false,
  ready: true,       // nothing to wait for when there is no provider
  signedIn: false,
  userId: null,
  getToken: null,
});

function useClerkAccount() {
  const { isLoaded, isSignedIn, userId, getToken } = useAuth();
  /* `ready` matters more than it looks. Clerk reports signed-out while it is
     still working out whether you are, so anything that acts on "not signed
     in" has to wait — otherwise every page load looks like a sign-out for a
     moment, and something that clears data on sign-out would clear it. */
  return {
    enabled: true,
    ready: !!isLoaded,
    signedIn: !!isSignedIn,
    userId: userId || null,
    getToken,
  };
}

/**
 * Who is signed in, if anyone.
 *
 * Picked once at module load rather than branched inside the hook, because
 * Clerk's own hooks throw without a provider above them and there is no
 * provider when accounts are off. `accountsEnabled` comes from a build-time
 * constant, so this is one implementation for the life of the bundle — the
 * rules of hooks are about a component changing which hooks it calls between
 * renders, and this cannot.
 */
export const useAccount = accountsEnabled ? useClerkAccount : () => SIGNED_OUT;

/** Wraps the app only when there is a key. Otherwise it is not in the tree. */
export function AuthProvider({ children }) {
  if (!accountsEnabled) return <>{children}</>;
  return <ClerkProvider publishableKey={CLERK_KEY}>{children}</ClerkProvider>;
}

/**
 * The control in the top bar.
 *
 * Absent rather than disabled when accounts are off — a sign-in button that
 * cannot sign anybody in is a worse answer than no button. Signed out it
 * offers, signed in it gets out of the way behind an avatar, because the
 * account is not what anyone came here to look at.
 */
export function AccountControls() {
  if (!accountsEnabled) return null;
  return (
    <div className="acct">
      <Show when="signed-out">
        <SignInButton mode="modal">
          <button type="button" className="acct-in">Sign in</button>
        </SignInButton>
      </Show>
      <Show when="signed-in">
        <UserButton afterSignOutUrl="/app" />
      </Show>
    </div>
  );
}
